import { inject, Injectable } from '@angular/core';
import { CampaignService } from './campaign.service';
import { WidgetService } from './widget.service';
import { OnsiteCampaign } from '../../campaign/campaign.model';
import { forkJoin, Observable } from 'rxjs';
import { map, share, tap } from 'rxjs/operators';
import { WidgetSimpleInfo } from '../../campaign/widgets.model';

@Injectable({
  providedIn: 'root',
})
export class WidgetMappingService {
  private readonly widgetService = inject(WidgetService);
  private _campaignIdToName: { number: string };
  private _widgetIdToCampaignId: { number: number };

  constructor(private campaignService: CampaignService) {}

  public getCampaignNameById(campaignId: number): string {
    if (!this._campaignIdToName) {
      return undefined;
    }
    return this._campaignIdToName[campaignId];
  }

  public getWidgetIdToCampaignIdMap(): { number: number } {
    // Callers index straight into the result, so an empty map degrades the names while undefined would throw.
    if (!this._widgetIdToCampaignId) {
      return {} as { number: number };
    }
    return JSON.parse(JSON.stringify(this._widgetIdToCampaignId));
  }

  public getCampaignIdToNameMap(): { number: string } {
    if (!this._campaignIdToName) {
      return {} as { number: string };
    }
    return JSON.parse(JSON.stringify(this._campaignIdToName));
  }

  public getCampaignIdByWidgetId(widgetId: number): number {
    if (!this._widgetIdToCampaignId) {
      return undefined;
    }
    return this._widgetIdToCampaignId[widgetId];
  }

  public getCampaignNameByWidgetId(widgetId: number): string {
    if (!this._campaignIdToName || !this._widgetIdToCampaignId) {
      return undefined;
    }
    const campaignId = this.getCampaignIdByWidgetId(widgetId);
    if (!campaignId) {
      return undefined;
    }
    return this._campaignIdToName[campaignId];
  }

  /** Callers that open a flyout must wait for this: the flyout reads the maps synchronously. */
  public loadMaps(widgets$?: Observable<Array<WidgetSimpleInfo>>): Observable<Array<WidgetSimpleInfo>> {
    return forkJoin([this.loadCampaignsData(), widgets$ ?? this.widgetService.getAllWidgetsSimpleInfo()]).pipe(
      map(([, widgets]) => widgets ?? []),
      tap((widgets) => this.loadWidgetsData(widgets)),
    );
  }

  public loadCampaignsData(): Observable<Array<OnsiteCampaign>> {
    const campaignIdToName = {} as { number: string };
    const request$ = this.campaignService.getAllCampaigns().pipe(share());
    request$.subscribe(
      (campaigns: Array<OnsiteCampaign>) => {
        if (campaigns) {
          campaigns.forEach((campaign) => (campaignIdToName[campaign.id] = campaign.name || ''));
        }
        this._campaignIdToName = campaignIdToName;
      },
      () => {
        this._campaignIdToName = undefined;
      },
    );
    return request$;
  }

  public loadWidgetsData(widgets: Array<WidgetSimpleInfo>): void {
    const widgetIdToCampaignId = {} as { number: number };
    if (widgets) {
      widgets.forEach((widget) => (widgetIdToCampaignId[widget.id] = widget.campaignId));
      this._widgetIdToCampaignId = widgetIdToCampaignId;
    } else {
      this._widgetIdToCampaignId = undefined;
    }
  }
}
